<template>
  <div class="room-page">
    <div class="room-header">
      <button class="back-btn" @click="goBack">&#8592; 返回</button>
      <div class="room-info">
        <h2>&#127968; {{ room?.id }}</h2>
        <span class="room-status" :class="room?.status?.toLowerCase()">
          {{ statusText }}
        </span>
      </div>
      <div class="ws-status" :class="{ connected: wsConnected }">
        {{ wsConnected ? '已连接' : '连接中...' }}
      </div>
    </div>

    <div class="room-meta-bar">
      <div class="meta-item">
        <span class="meta-label">&#128101; 人数</span>
        <span class="meta-value">{{ room?.players?.length }} / {{ room?.maxPlayers }}</span>
      </div>
      <div class="meta-divider"></div>
      <div class="meta-item">
        <span class="meta-label">&#127919; 房间限制</span>
        <span class="meta-value">{{ roomLimitText }}</span>
      </div>
      <div class="meta-divider"></div>
      <div class="meta-item">
        <span class="meta-label">&#9201; 摆牌时长</span>
        <span class="meta-value">{{ room?.arrangeTime }}秒</span>
      </div>
    </div>

    <div class="room-body">
      <div class="players-card">
        <h3>&#128101; 玩家列表</h3>
        <div class="player-list">
          <div
            v-for="player in room?.players"
            :key="player.id"
            class="player-item"
            :class="{
              'is-me': isMe(player),
              'is-owner': player.id === room?.ownerId,
              'offline': player.isOnline === false
            }"
          >
            <div class="player-avatar-wrapper">
              <div
                class="player-avatar"
                :class="{ clickable: isMe(player) }"
                :title="isMe(player) ? '点击修改昵称' : ''"
                @click="isMe(player) && openNicknameModal()"
              >
                <ChatBubble :bubble="chatBubbles[player.id]" />
                {{ player.avatar === 'player' ? '&#129333;' : '&#129302;' }}
              </div>
              <div class="player-score">{{ player.totalScore || 0 }}</div>
            </div>
            <div class="player-detail">
              <div class="player-name">
                {{ player.name }}
                <span v-if="player.id === room?.ownerId" class="owner-tag">&#127968;房主</span>
                <span v-if="isMe(player)" class="me-tag">&#128073;我</span>
              </div>
              <div class="player-status">
                <span v-if="player.isOnline === false" class="offline-text">离线</span>
                <span v-else-if="player.isReady" class="ready-text">已准备</span>
                <span v-else class="not-ready-text">未准备</span>
              </div>
            </div>
            <div v-if="isMe(player) && room?.status === 'WAITING'" class="player-actions">
              <button
                class="ready-btn"
                :class="{ ready: player.isReady }"
                @click="toggleReady"
              >
                {{ player.isReady ? '取消准备' : '准备' }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div v-if="room?.status === 'WAITING'" class="start-area">
        <button
          v-if="isOwner"
          class="start-game-btn"
          :disabled="!canStartGame"
          @click="startGame"
        >
          开始游戏
        </button>
        <div class="start-footer">
          <p v-if="isOwner && !canStartGame" class="hint">需要所有玩家准备后才能开始</p>
          <span v-else class="hint-spacer"></span>
          <button class="invite-link-btn" type="button" @click="showInviteModal = true">
            邀请链接
          </button>
        </div>
      </div>
    </div>

    <InviteModal
      :visible="showInviteModal"
      :user-id="playerId"
      :user-name="playerName"
      :room-id="room && room.id"
      @close="showInviteModal = false"
    />
    <NicknameModal
      :visible="showNicknameModal"
      v-model="nicknameInput"
      :submitting="nicknameSubmitting"
      @close="closeNicknameModal"
      @confirm="submitNicknameChange"
    />
    <ChatPanel
      v-model="chatInput"
      :open="chatOpen"
      :messages="chatMessages"
      :self-id="chatSelfId"
      @toggle="chatOpen = !chatOpen"
      @send="sendChat"
    />
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import ws from '@/api/ws'
import roomApi from '@/api/room'
import InviteModal from '@/components/InviteModal.vue'
import NicknameModal from '@/components/NicknameModal.vue'
import ChatPanel from '@/components/ChatPanel.vue'
import ChatBubble from '@/components/ChatBubble.vue'
import nicknameMixin from '@/mixins/nickname'
import chatMixin from '@/mixins/chat'

export default {
  name: 'RoomPage',
  components: {
    InviteModal,
    NicknameModal,
    ChatPanel,
    ChatBubble
  },
  mixins: [nicknameMixin, chatMixin],
  data() {
    return {
      wsConnected: false,
      pollInterval: null,
      showInviteModal: false
    }
  },
  computed: {
    ...mapState('multiplayer', ['room']),
    ...mapState(['playerId', 'playerName']),
    ...mapGetters('multiplayer', ['isOwner']),
    statusText() {
      const map = { WAITING: '等待中', PLAYING: '游戏中', SETTLING: '结算中' }
      return map[this.room?.status] || this.room?.status
    },
    canStartGame() {
      if (!this.room) return false
      return this.room.players.length >= 2 && this.room.players.every(p => p.isReady)
    },
    roomLimitText() {
      if (!this.room) return ''
      if (this.room.limitType === 'time' || Number(this.room.durationMinutes) > 0) {
        return this.formatDurationLabel(Number(this.room.durationMinutes) || 30)
      }
      return `${this.room.roundCount || 20}局`
    }
  },
  mounted() {
    if (!this.room) {
      this.$router.push('/')
      return
    }
    this.setupChat()
    this.connectWS()
    this.startPolling()
    this.setupWSListeners()
  },
  beforeDestroy() {
    this.stopPolling()
    ws.off('DEAL', this.onDeal)
    ws.off('GAME_START', this.onGameStart)
    ws.off('PLAYER_JOINED', this.onRoomUpdate)
    ws.off('PLAYER_LEFT', this.onRoomUpdate)
    ws.off('PLAYER_READY', this.onRoomUpdate)
    ws.off('PLAYER_RENAMED', this.onRoomUpdate)
    ws.off('CONFIRM_LAYOUT', this.onRoomUpdate)
  },
  methods: {
    isMe(player) {
      return player.id === this.playerId
    },
    connectWS() {
      const player = this.room.players.find(p => p.id === this.playerId)
      if (!player) return
      ws.connect(player.id, this.room.id, () => {
        this.wsConnected = true
        this.$store.commit('multiplayer/SET_WS_CONNECTED', true)
      }, () => {
        this.wsConnected = false
        this.$store.commit('multiplayer/SET_WS_CONNECTED', false)
      })
    },
    setupWSListeners() {
      ws.on('DEAL', this.onDeal)
      ws.on('GAME_START', this.onGameStart)
      ws.on('PLAYER_JOINED', this.onRoomUpdate)
      ws.on('PLAYER_LEFT', this.onRoomUpdate)
      ws.on('PLAYER_READY', this.onRoomUpdate)
      ws.on('PLAYER_RENAMED', this.onRoomUpdate)
      ws.on('CONFIRM_LAYOUT', this.onRoomUpdate)
    },
    onDeal(msg) {
      this.$store.commit('multiplayer/SET_DEAL_PAYLOAD', msg)
    },
    onGameStart(msg = {}) {
      const payload = msg.payload || {}
      if (this.room && Object.keys(payload).length > 0) {
        const nextRoom = {
          ...this.room,
          gameStatus: payload.status || this.room.gameStatus,
          round: payload.round || this.room.round,
          roundCount: payload.roundCount !== undefined ? payload.roundCount : this.room.roundCount,
          limitType: payload.limitType || this.room.limitType,
          durationMinutes: payload.durationMinutes !== undefined ? payload.durationMinutes : this.room.durationMinutes,
          expireTime: payload.expireTime !== undefined ? payload.expireTime : this.room.expireTime,
          extraSuits: payload.extraSuits || this.room.extraSuits || [],
          countdown: payload.countdown !== undefined ? payload.countdown : this.room.countdown
        }
        ;[
          'baseScore',
          'arrangeTime',
          'enableSpecialHands',
          'enableHomeRun',
          'allowOddCards',
          'allowFouled',
          'enableHorse',
          'horseSuit',
          'horseRank',
          'enableYucai',
          'yucaiScore',
          'allowRoomRate',
          'roomRatePercent',
          'dragonScore',
          'allBigScore',
          'allSmallScore',
          'sixPairsHalfScore',
          'threeFlushScore',
          'threeStraightScore',
          'sweepScoreType',
          'sweepScoreValue'
        ].forEach(key => {
          if (payload[key] !== undefined && payload[key] !== null) {
            nextRoom[key] = payload[key]
          }
        })
        this.$store.commit('multiplayer/UPDATE_ROOM', nextRoom)
      }
      this.$router.push('/game')
    },
    formatDurationLabel(minutes) {
      if (minutes === 30) return '半小时'
      if (minutes % 60 === 0) return `${minutes / 60}小时`
      return `${minutes}分钟`
    },
    async onRoomUpdate() {
      try {
        const res = await roomApi.getRoom(this.room.id, this.$store.state.playerId)
        if (res.success) {
          this.$store.commit('multiplayer/UPDATE_ROOM', res.data)
        }
      } catch (e) {
        console.error('Poll room error:', e)
      }
    },
    startPolling() {
      this.pollInterval = setInterval(() => {
        this.onRoomUpdate()
      }, 3000)
    },
    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval)
        this.pollInterval = null
      }
    },
    async toggleReady() {
      const player = this.room.players.find(p => p.id === this.playerId)
      if (!player) return
      try {
        await this.$store.dispatch('multiplayer/setReady', !player.isReady)
        this.onRoomUpdate()
      } catch (e) {
        if (!e.handled) this.$message.error(e.message || '设置准备状态失败')
      }
    },
    async startGame() {
      try {
        await this.$store.dispatch('multiplayer/sendStartGame')
      } catch (e) {
        if (!e.handled) this.$message.error(e.message || '开始游戏失败')
      }
    },
    async goBack() {
      await this.$store.dispatch('multiplayer/leaveRoom')
      this.$router.push('/')
    }
  }
}
</script>

<style lang="scss" scoped>
.room-page {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
  display: flex;
  flex-direction: column;
  color: white;
}

.room-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 24px;
  background: rgba(0, 0, 0, 0.3);

  .back-btn {
    padding: 6px 14px;
    border: none;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.1);
    color: white;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }

  .room-info {
    text-align: center;

    h2 {
      margin: 0;
      font-size: 18px;
      color: #ffd700;
    }

    .room-status {
      font-size: 12px;
      padding: 2px 8px;
      border-radius: 10px;
      background: rgba(255, 255, 255, 0.1);

      &.waiting { color: #ffd700; }
      &.playing { color: #27ae60; }
      &.settling { color: #3498db; }
    }
  }

  .ws-status {
    font-size: 11px;
    padding: 3px 8px;
    border-radius: 10px;
    background: rgba(231, 76, 60, 0.2);
    color: #e74c3c;

    &.connected {
      background: rgba(39, 174, 96, 0.2);
      color: #27ae60;
    }
  }
}

.room-meta-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 8px 24px;
  background: rgba(0, 0, 0, 0.15);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  .meta-item {
    display: flex;
    align-items: center;
    gap: 6px;

    .meta-label {
      font-size: 12px;
      color: rgba(255, 255, 255, 0.5);
    }

    .meta-value {
      font-size: 13px;
      color: #ffd700;
      font-weight: bold;
    }
  }

  .meta-divider {
    width: 1px;
    height: 14px;
    background: rgba(255, 255, 255, 0.15);
  }
}

.room-body {
  flex: 1;
  padding: 12px 20px;
  overflow-y: auto;
  max-width: 600px;
  width: 100%;
  margin: 0 auto;
}

.players-card {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  padding: 12px 16px;

  h3 {
    margin: 0 0 10px 0;
    font-size: 14px;
    color: rgba(255, 255, 255, 0.6);
  }
}

.player-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.player-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  border: 1px solid transparent;
  transition: all 0.3s ease;

  &.is-me {
    border-color: rgba(255, 215, 0, 0.3);
    background: rgba(255, 215, 0, 0.05);
  }

  &.offline {
    opacity: 0.6;
  }

  .player-avatar-wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;

    .player-avatar {
      position: relative;
      font-size: 24px;

      &.clickable {
        cursor: pointer;
        border-radius: 50%;
        transition: transform 0.15s ease;

        &:hover {
          transform: scale(1.08);
        }
      }
    }

    .player-score {
      font-size: 11px;
      font-weight: bold;
      color: #ffd700;
      background: rgba(0, 0, 0, 0.3);
      padding: 1px 6px;
      border-radius: 8px;
      min-width: 20px;
      text-align: center;
    }
  }

  .player-detail {
    flex: 1;

    .player-name {
      font-weight: bold;
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 5px;

      .owner-tag {
        font-size: 10px;
        padding: 1px 5px;
        border-radius: 4px;
        background: #e74c3c;
        color: white;
      }

      .me-tag {
        font-size: 10px;
        padding: 1px 5px;
        border-radius: 4px;
        background: #ffd700;
        color: #1a1a2e;
      }
    }

    .player-status {
      font-size: 11px;
      margin-top: 1px;

      .ready-text { color: #27ae60; }
      .not-ready-text { color: rgba(255, 255, 255, 0.4); }
      .offline-text { color: #e74c3c; }
    }
  }

  .player-actions {
    .ready-btn {
      padding: 5px 12px;
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 5px;
      background: rgba(255, 255, 255, 0.1);
      color: white;
      font-size: 12px;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.2);
      }

      &.ready {
        border-color: #27ae60;
        background: rgba(39, 174, 96, 0.2);
        color: #27ae60;
      }
    }
  }
}

.start-area {
  text-align: center;
  margin-top: 12px;

  .start-game-btn {
    width: 100%;
    padding: 14px;
    border: none;
    border-radius: 10px;
    background: linear-gradient(135deg, #27ae60, #2ecc71);
    color: white;
    font-size: 16px;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.3s ease;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 4px 15px rgba(39, 174, 96, 0.4);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }

  .start-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-top: 8px;
    min-height: 28px;
  }

  .hint {
    flex: 1;
    margin: 0;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.5);
    text-align: left;
    line-height: 1.4;
  }

  .hint-spacer {
    flex: 1;
  }

  .invite-link-btn {
    flex-shrink: 0;
    padding: 4px 10px;
    border: 1px solid rgba(255, 215, 0, 0.45);
    border-radius: 6px;
    background: rgba(255, 215, 0, 0.12);
    color: #ffd700;
    font-size: 12px;
    cursor: pointer;

    &:hover {
      background: rgba(255, 215, 0, 0.22);
    }
  }
}
</style>
